import { useLazyQuery } from '@apollo/client';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { GetTestResultsByControlIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ContentTable } from 'pdfmake/interfaces';
import { useTranslation } from 'react-i18next';
import type { GetStandardFormFieldLabel } from 'src/hooks/forms/useFormCustomisation';

import { toLocalDate } from '@/utils/dateUtils';
import { createTable, tableHeaders } from '@/utils/pdf/table';
import { useFormConfigRegistry } from '@/utils/table/hooks/form/useFormConfigRegistry';

const usePerformanceTableExporter = (
  controlId: string,
  getStandardFieldLabel: GetStandardFormFieldLabel
): [() => Promise<ContentTable>, boolean] => {
  const { t } = useTranslation(['common']);

  const [getTestResults, getTestResultsResult] = useLazyQuery(
    GetTestResultsByControlIdDocument,
    {
      variables: {
        controlId: controlId,
      },
    }
  );
  const { getLabel: getEffectivenessLabel } = useRating('effectiveness');

  const formRegistry = useFormConfigRegistry();
  const testTypeAvailable =
    formRegistry.test_result.TestType.available !== false;

  const createExportTable = async () => {
    const { data: testResultData } = await getTestResults();

    const testTypeLookup = t('testTypes');
    const rows = testResultData?.test_result ?? [];

    type Row = (typeof rows)[number];

    const columns: {
      visible: boolean;
      width: string | number;
      header: string;
      cell: (row: Row) => string | null | undefined;
    }[] = [
      {
        visible: true,
        width: '*',
        header: getStandardFieldLabel('test_result', 'Title'),
        cell: (au) => au.Title ?? '-',
      },
      {
        visible: true,
        width: 50,
        header: getStandardFieldLabel('test_result', 'TestDate'),
        cell: (au) => toLocalDate(au.TestDate),
      },
      {
        visible: testTypeAvailable,
        width: 70,
        header: getStandardFieldLabel('test_result', 'TestType'),
        cell: (au) =>
          au.TestType
            ? testTypeLookup[au.TestType as keyof typeof testTypeLookup]
            : '-',
      },
      {
        visible: true,
        width: 90,
        header: getStandardFieldLabel('test_result', 'OverallEffectiveness'),
        cell: (au) => getEffectivenessLabel(au.OverallEffectiveness),
      },
      {
        visible: true,
        width: 70,
        header: getStandardFieldLabel('test_result', 'Submitter'),
        cell: (au) => au.submitter?.FriendlyName ?? '',
      },
      {
        visible: true,
        width: 50,
        header: getStandardFieldLabel('assessment', 'NextTestDate'),
        cell: (au) =>
          toLocalDate(
            au.assessmentParents
              .filter((c) => c.assessment)
              .map((c) => c.assessment)[0]?.NextTestDate
          ) ?? '',
      },
    ];

    const visible = columns.filter((c) => c.visible);

    return createTable({
      widths: visible.map((c) => c.width),
      body: [
        tableHeaders(visible.map((c) => c.header)),
        ...rows.map((au) => visible.map((c) => c.cell(au) ?? '')),
      ],
    });
  };

  return [createExportTable, getTestResultsResult.loading];
};

export default usePerformanceTableExporter;
